import React from 'react';
import ReactECharts from 'echarts-for-react';
import type { RevenueReportData, RevenueMetric } from '../../../../types/chat-message';
import { ReportActionButtons } from './ReportActionButtons';
import './RevenueReport.scss';

const CHART_COLORS = ['#8B5CF6', '#F87171', '#60A5FA', '#34D399', '#FBBF24', '#A78BFA'];


interface RevenueReportProps {
  data: RevenueReportData;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  isLastMessage?: boolean;
  isFromHistory?: boolean;
  text?: any;
  buttons?: any;
}

const MetricRow: React.FC<{ label: string; metric: RevenueMetric }> = ({ label, metric }) => {
  const isUp = metric.direction === 'up';
  const isDown = metric.direction === 'down';
  const hasIndicator = isUp || isDown;

  return (
    <div className="rr-metric-row">
      <div className="rr-metric-label-group">
        <span className="rr-metric-label">{label}</span>
        {hasIndicator && (
          <span className={`rr-metric-change ${isUp ? 'up' : 'down'}`}>
            {isUp ? '▲' : '▼'} {metric.rate.toFixed(2)}%
          </span>
        )}
      </div>
      <span className="rr-metric-value">{(metric.formattedValue ?? '').replace(' VND', ' đ')}</span>
    </div>
  );
};

export const RevenueReport: React.FC<RevenueReportProps> = ({
  data,
  buttons,
  onButtonClick,
  isLastMessage,
  isFromHistory,
  text,
}) => {
  const { startDate, endDate, revenue, orders, customers, topProducts = [] } = data ?? {};
  const isButtonDisabled = !isLastMessage || !!isFromHistory;

  const dateLabel = startDate === endDate
    ? startDate
    : `${startDate} - ${endDate}`;

  const chartData = topProducts.map((p, i) => ({
    name: p.name,
    value: p.amount,
    amount: p.amount,
    quantity: p.quantity,
    itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
  }));

  const chartOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const num = typeof params.data.amount === 'number' ? params.data.amount : parseFloat(String(params.data.amount ?? '0'));
        return `<b>${(isNaN(num) ? 0 : Math.round(num)).toLocaleString('vi-VN')}</b>`;
      },
      confine: true,
    },
    grid: {
      containLabel: true,
    },
    series: [
      {
        type: 'pie',
        radius: ['35%', '85%'],
        avoidLabelOverlap: false,
        label: { show: false },
        itemGap: 0,
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        emphasis: {
          scale: true,
          label: { show: false },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
          },
        },
        data: chartData,
      },
    ],
  };

  return (
    <div className="revenue-report">
      <div className="rr-header">
        <span className="rr-title">{text || `DOANH THU BÁN HÀNG ${dateLabel || ''}`}</span>
      </div>

      <div className="rr-metrics">
        {revenue && <MetricRow label="Doanh thu" metric={revenue} />}
        {orders && <MetricRow label="Đơn hàng" metric={orders} />}
        {customers && <MetricRow label="Khách hàng" metric={customers} />}
      </div>

      {topProducts.length > 0 && (
        <>
          <span className="rr-products-label">Hàng hóa bán chạy</span>
          <ReactECharts
            option={chartOption}
            style={{ height: '180px', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
          <div className="rr-legend">
            {topProducts.map((p, i) => (
              <div key={i} className="rr-legend-item">
                <span
                  className="rr-legend-dot"
                  style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                />
                <span className="rr-legend-name">{p.name}</span>
                <span className="rr-legend-qty">{p.quantity}</span>
              </div>
            ))}
            {/* <div className="rr-legend-item"><div className="rr-legend-dot"  style={{ backgroundColor: CHART_COLORS[topProducts && topProducts.length % CHART_COLORS.length] }}></div><span className="rr-legend-name" >Khác</span></div> */}
          </div>
        </>
      )}

      <ReportActionButtons
        buttons={buttons}
        isDisabled={isButtonDisabled}
        onButtonClick={onButtonClick}
        reportType="Doanh thu bán hàng"
      />
    </div>
  );
};
